import type { IKlipyGif, IKlipyListResponse } from './types';

const KLIPY_API_BASE = 'https://api.klipy.com/api/v1';

interface IKlipyListParams {
  page?: number;
  perPage?: number;
  customerId?: string;
  locale?: string;
  query?: string;
}

const buildListUrl = (
  apiKey: string,
  endpoint: 'trending' | 'search',
  params: IKlipyListParams
): string => {
  const searchParams = new URLSearchParams();
  searchParams.set('page', String(params.page ?? 1));
  searchParams.set('per_page', String(params.perPage ?? 24));
  searchParams.set('content_filter', 'medium');

  if (params.customerId) {
    searchParams.set('customer_id', params.customerId);
  }

  if (params.locale) {
    searchParams.set('locale', params.locale);
  }

  if (endpoint === 'search' && params.query) {
    searchParams.set('q', params.query);
  }

  return `${KLIPY_API_BASE}/${apiKey}/gifs/${endpoint}?${searchParams.toString()}`;
};

const parseListResponse = (payload: IKlipyListResponse): IKlipyGif[] => {
  if (!payload?.result || !payload.data?.data) {
    return [];
  }

  return payload.data.data.filter((item) => item?.slug);
};

export const KlipyApi = {
  gifs: {
    async trending(apiKey: string, params: IKlipyListParams = {}): Promise<IKlipyGif[]> {
      const response = await fetch(buildListUrl(apiKey, 'trending', params));
      if (!response.ok) {
        throw new Error(`Klipy trending request failed (${response.status})`);
      }

      return parseListResponse((await response.json()) as IKlipyListResponse);
    },

    async search(apiKey: string, params: IKlipyListParams & { query: string }): Promise<IKlipyGif[]> {
      const response = await fetch(buildListUrl(apiKey, 'search', params));
      if (!response.ok) {
        throw new Error(`Klipy search request failed (${response.status})`);
      }

      return parseListResponse((await response.json()) as IKlipyListResponse);
    },

    async itemsBySlugs(apiKey: string, slugs: string[]): Promise<IKlipyGif[]> {
      if (slugs.length === 0) return [];

      const searchParams = new URLSearchParams();
      searchParams.set('slugs', slugs.join(','));

      const response = await fetch(
        `${KLIPY_API_BASE}/${apiKey}/gifs/items?${searchParams.toString()}`
      );

      if (!response.ok) {
        throw new Error(`Klipy items request failed (${response.status})`);
      }

      return parseListResponse((await response.json()) as IKlipyListResponse);
    },

    async triggerShare(apiKey: string, slug: string, query?: string): Promise<void> {
      const searchParams = new URLSearchParams();
      if (query?.trim()) {
        searchParams.set('q', query.trim());
      }

      const queryString = searchParams.toString();
      const url = `${KLIPY_API_BASE}/${apiKey}/gifs/share/${encodeURIComponent(slug)}${
        queryString ? `?${queryString}` : ''
      }`;

      const response = await fetch(url, { method: 'POST' });
      if (!response.ok) {
        throw new Error(`Klipy share trigger failed (${response.status})`);
      }
    },
  },
};
